import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import PersonCard from "../components/PersonCard";
import Modal from "../components/Modal";
import AddPersonForm from "../components/AddPersonForm";
import ViewPersonModal from "../components/ViewPersonModal";
import { db, auth } from "../firebase";
import PageHeader from "../components/PageHeader";
import { FiGrid, FiList } from "react-icons/fi";

function RosterPage({ customFields, setCustomFields }) {
  const { rosterId } = useParams();
  const [roster, setRoster] = useState(null);
  const [people, setPeople] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [filterType, setFilterType] = useState("metOn"); // metOn or tags
  const [filterValue, setFilterValue] = useState("All");
  const [tags, setTags] = useState([]);

  useEffect(() => {
    console.log("Current roster state:", roster);
  }, [roster]);

  useEffect(() => {
    const fetchRosterAndPeople = async () => {
      try {
        const userId = auth.currentUser?.uid;
        if (!userId) {
          console.error("No user logged in");
          return;
        }

        const rosterRef = doc(db, "users", userId, "rosters", rosterId);
        const rosterSnap = await getDoc(rosterRef);

        if (rosterSnap.exists()) {
          const rosterData = rosterSnap.data();
          setRoster(rosterData);
          setCustomFields(rosterData.customFields || []);
        } else {
          console.error("Roster not found");
        }

        const peopleRef = collection(db, "users", userId, "rosters", rosterId, "people");
        const peopleSnap = await getDocs(peopleRef);

        if (!peopleSnap.empty) {
          const rosterPeople = peopleSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPeople(rosterPeople);
        } else {
          console.log("No people found for this roster");
          setPeople([]);
        }
      } catch (error) {
        console.error("Error fetching roster or people:", error);
      }
    };

    const fetchTags = async () => {
      try {
        const tagsCollection = collection(db, "globalTags");
        const tagSnapshot = await getDocs(tagsCollection);
        const fetchedTags = tagSnapshot.docs.map((doc) => doc.data().name);
        setTags(fetchedTags);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };

    fetchRosterAndPeople();
    fetchTags();
  }, [rosterId, setCustomFields]);

  const sortedAndFilteredPeople = () => {
    const peopleWithPoints = people.map((person) => {
      const totalPoints =
        roster?.customFields?.reduce((sum, field) => {
          const value = parseFloat(person.customFields?.[field.name] || 0);
          return sum + (isNaN(value) ? 0 : value);
        }, 0) || 0;

      return { ...person, totalPoints };
    });

    const sortedPeople = peopleWithPoints.sort((a, b) => b.totalPoints - a.totalPoints);

    if (filterType === "metOn") {
      if (filterValue === "All") return sortedPeople;
      return sortedPeople.filter((person) => person.metOn === filterValue);
    }

    if (filterType === "tags") {
      if (filterValue === "All") return sortedPeople;
      return sortedPeople.filter((person) => person.tags?.includes(filterValue));
    }

    return sortedPeople;
  };

  return (
    <>
      <PageHeader title={roster?.name} />
      <div className="container">
        <div
          style={{
            marginTop: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* Left side: Filtering */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setFilterValue("All");
              }}
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid #ddd",
              }}
            >
              <option value="metOn">Filter by Met On</option>
              <option value="tags">Filter by Tags</option>
            </select>

            {filterType === "metOn" && (
              <select
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #ddd",
                }}
              >
                <option value="All">All</option>
                <option value="Hinge">Hinge</option>
                <option value="Raya">Raya</option>
                <option value="Tinder">Tinder</option>
                <option value="Feeld">Feeld</option>
                <option value="Bumble">Bumble</option>
                <option value="Real Life">Real Life</option>
              </select>
            )}

            {filterType === "tags" && (
              <select
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                style={{
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #ddd",
                }}
              >
                <option value="All">All</option>
                {tags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            )}

            {/* Toggle View Mode */}
            <button
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
              aria-label="Toggle View Mode"
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                paddingLeft: "2px",
              }}
            >
              {viewMode === "grid" ? <FiList /> : <FiGrid />}
            </button>
          </div>

          {/* Right side: Add Person Button */}
          <button
  className="add-person-btn"
  onClick={() => setIsModalOpen(true)}
  aria-label="Add Person"
  style={{
    backgroundColor: "black",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    fontSize: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    transform: "translateX(-20px)",
  }}
>
  +
</button>

        </div>

        <div
          className={viewMode === "grid" ? "grid" : "list"}
          style={{
            display: "grid",
            gridTemplateColumns: viewMode === "grid" ? "repeat(auto-fill, minmax(160px, 1fr))" : "1fr",
            marginTop: "20px",
          }}
        >
          {sortedAndFilteredPeople().map((person) => (
            <PersonCard
              key={person.id}
              person={{ ...person, rosterId }}
              viewMode={viewMode}
              previewFields={roster?.previewFields || []}
              previewTags={roster?.previewTags || false} 
              onClick={() =>
                setSelectedPerson({
                  ...person,
                  rosterId: rosterId,
                })
              }
            />

          ))}
        </div>

        {isModalOpen && (
          <Modal closeModal={() => setIsModalOpen(false)}>
            <AddPersonForm
              updateRosterPeople={(updatedPeople) => setPeople(updatedPeople)}
              people={people}
              customFields={roster?.customFields || []}
              previewFields={roster?.previewFields || []}
              previewTags={roster?.previewTags || false} 
              closeModal={() => setIsModalOpen(false)}
              rosterId={rosterId}
            />
          </Modal>
        )}

        {selectedPerson && (
          <ViewPersonModal
          person={selectedPerson}
          customFields={roster?.customFields || []}
          onClose={() => setSelectedPerson(null)}
          onSave={(updatedPerson) => {
            const updatedPeople = people.map((p) =>
              p.id === updatedPerson.id ? updatedPerson : p
            );
            setPeople(updatedPeople);
            setSelectedPerson(null);
          }}
          onDelete={(id) => {
            const updatedPeople = people.filter((p) => p.id !== id);
            setPeople(updatedPeople);
            setSelectedPerson(null);
          }}
          setPeople={setPeople} // Pass the setPeople function here
        />

        )}
      </div>
    </>
  );
}

export default RosterPage;
